<template>
  <auth-shell>
    <p class="auth-form__eyebrow">
      ¡Bienvenido!
    </p>
    <h1 class="auth-form__title">
      Iniciar sesión
    </h1>

    <validation-observer
      ref="loginForm"
      #default="{ invalid }"
    >
      <b-form
        class="auth-form__fields"
        @submit.prevent="login"
      >
        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Correo electrónico"
          vid="email"
          rules="required|email"
        >
          <label
            class="sr-only"
            for="login-email"
          >Correo electrónico</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="MailIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="login-email"
              v-model="userEmail"
              type="email"
              autocomplete="email"
              placeholder="Correo electrónico"
            />
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Contraseña"
          vid="password"
          rules="required"
        >
          <label
            class="sr-only"
            for="login-password"
          >Contraseña</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="LockIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="login-password"
              v-model="password"
              :type="passwordFieldType"
              autocomplete="current-password"
              placeholder="Contraseña"
            />
            <b-input-group-append is-text>
              <button
                type="button"
                class="auth-input__toggle"
                :aria-label="passwordFieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                @click="togglePasswordVisibility"
              >
                <feather-icon :icon="passwordToggleIcon" />
              </button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <div class="auth-form__forgot">
          <b-link :to="{ name: 'auth-forgot-password' }">
            ¿Olvidaste tu contraseña?
          </b-link>
        </div>

        <b-button
          type="submit"
          class="auth-submit"
          block
          :disabled="invalid || isSubmitting"
        >
          <b-spinner
            v-if="isSubmitting"
            small
          />
          <template v-else>
            <span>Iniciar sesión</span>
            <feather-icon
              icon="ArrowRightIcon"
              size="18"
            />
          </template>
        </b-button>
      </b-form>
    </validation-observer>

    <template v-if="googleEnabled">
      <div class="auth-divider">
        <span>o continúa con</span>
      </div>
      <google-sign-in-button @success="handleAuthSuccess" />
    </template>

    <p class="auth-form__footer">
      ¿No tienes cuenta?
      <b-link :to="{ name: 'auth-register' }">
        Regístrate aquí
      </b-link>
    </p>
  </auth-shell>
</template>

<script>
import { mapActions } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink,
  BFormInput,
  BInputGroupAppend,
  BInputGroupPrepend,
  BInputGroup,
  BForm,
  BButton,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { startSession, landingRoute, googleSignInEnabled } from '@/auth/session'
import AuthShell from '@/components/auth/AuthShell.vue'
import GoogleSignInButton from '@/components/auth/GoogleSignInButton.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    AuthShell,
    GoogleSignInButton,
    BLink,
    BFormInput,
    BInputGroupAppend,
    BInputGroupPrepend,
    BInputGroup,
    BForm,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      status: '',
      password: '',
      userEmail: '',
      isSubmitting: false,
      googleEnabled: googleSignInEnabled(),

      // validation rules
      required,
      email,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    ...mapActions('auth', ['signIn']),
    getErrorText(error) {
      const messages = error && error.response && error.response.data && error.response.data.messages
      if (Array.isArray(messages) && messages.length > 0) return messages.join(' ')
      if (typeof messages === 'string') return messages
      return 'No fue posible iniciar sesión.'
    },
    // La ruta guardada la dejo la sesion anterior, que pudo ser de otro rol. Mandar ahi a
    // quien acaba de entrar lo deja en una pantalla que no puede abrir: error de permisos y
    // sin su menu, porque esa vista nunca carga su contexto.
    canOpenRoute(path, userData) {
      try {
        const { meta } = this.$router.resolve(path).route

        if (meta?.requiresAdmin && userData?.role_name !== 'admin') return false
        if (meta?.requiresSupplierAdmin && userData?.role_name !== 'supplier_admin') return false

        return true
      } catch (error) {
        return false
      }
    },

    handleAuthSuccess(response) {
      const userData = startSession(response)

      const redirectPath = localStorage.getItem('redirectAfterLogin')
      localStorage.removeItem('redirectAfterLogin')

      if (redirectPath && this.canOpenRoute(redirectPath, userData)) {
        const requiresHardRedirect = /^\/(checkout|pago)\//.test(redirectPath)
        if (requiresHardRedirect) {
          window.location.replace(redirectPath)
          return
        }
        this.$router.replace(redirectPath)
      } else {
        this.$router.replace(landingRoute(userData))
      }
    },
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (!success || this.isSubmitting) return

        this.isSubmitting = true
        this.signIn({ email: this.userEmail, password: this.password })
          .then(response => {
            this.handleAuthSuccess(response)
          })
          .catch(error => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-center',
              props: {
                title: 'Error',
                icon: 'CoffeeIcon',
                variant: 'danger',
                text: this.getErrorText(error),
              },
            })
          })
          .finally(() => {
            this.isSubmitting = false
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../components/auth/auth-page.scss';
</style>
